import type { CSSProperties, ReactNode } from "react";
import { chipSelectable, scroller } from "@web/theme/theme";

export type SelectChipProps = { selected: boolean; onClick: () => void; disabled?: boolean; /** Color dot (venue hue). */ color?: string | null; children: ReactNode };

/** Tappable filter chip (venue / status filters). */
export function SelectChip({ selected, onClick, disabled = false, color, children }: SelectChipProps) {
  return (
    <button type="button" aria-pressed={selected} disabled={disabled} onClick={onClick} className={`${chipSelectable.base} ${selected ? chipSelectable.selected : chipSelectable.unselected}`}>
      {color && <span aria-hidden style={{ "--bar": color } as CSSProperties} className={chipSelectable.dot} />}
      {children}
    </button>
  );
}

/** Horizontal, scrollable strip of chips that bleeds into the gutters. */
export function ChipStrip({ children, label = "Filters", wrap = false }: { children: ReactNode; label?: string; wrap?: boolean }) {
  return (
    <div role="group" aria-label={label} className={wrap ? "v365-filter-chips-wrap" : scroller} onFocusCapture={event => {
      // Native focus scrolling can leave a partially visible final chip cut
      // off. Reveal the whole keyboard target without moving unrelated rows.
      if (!wrap && event.target instanceof HTMLElement) event.target.scrollIntoView({ block: "nearest", inline: "nearest" });
    }}>
      {children}
    </div>
  );
}

/** A venue-colored dot for rows and legends. */
export function ColorDot({ color, label }: { color?: string | null; label?: string }) {
  return <span role={label ? "img" : undefined} aria-label={label} aria-hidden={label ? undefined : true} style={{ "--bar": color ?? undefined } as CSSProperties} className={chipSelectable.dot} />;
}
